import React, { useEffect, useState } from 'react';
import { Typography, Box } from '@material-ui/core';
import { TFunction } from 'react-i18next';
import { UseMutationResult } from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { ISubmitEvent } from '@rjsf/core';
import RJSF from '../RJSF';
import { canOnlyReadExtMetadata, generateJSONSchema, generateUISchema } from '../../utils';
import { useSnackbar } from '../../providers/Snackbar';

import {
  AssetReadExternal,
  AssetTypeReadExternal,
  AssetTypeReadExternalExtMetadataConfig,
  ErrorModel,
  OrgReadExternal,
  OrgTypeExtMetadataReadExternal,
  OrgUpdate,
  ProfileReadExternal,
  RoleExtMetadataReadExternal,
  RoleExtMetadataReadExternalExtMetadataConfig,
  UserReadExternal,
  UserUpdateExternal,
} from '@askporter/grieg-types';
import { useParams } from 'react-router-dom';
import { UserRole } from '../../types';
import { BasicPageTemplate, LoadingButton } from '..';
import { Check } from '@material-ui/icons';

interface ExtendedMetadataProps {
  t: TFunction<'translation'>;
  isSmallDevice: boolean;
  data: ProfileReadExternal | OrgReadExternal | UserReadExternal | AssetReadExternal;
  extendedMetadataConfig:
    | RoleExtMetadataReadExternal
    | OrgTypeExtMetadataReadExternal
    | { extMetadataConfig: AssetTypeReadExternal['extMetadataConfig'] };
  updateDataMutation: UseMutationResult<
    AxiosResponse<ProfileReadExternal | OrgUpdate | UserReadExternal | AssetReadExternal>,
    AxiosError<ErrorModel>,
    ProfileReadExternal | OrgUpdate | UserUpdateExternal
  >;
  currentUserRole: UserRole;
}

/**
 * Renders the Extended metadata page
 *
 * @param t - Translation function
 * @param isSmallDevice - true / false flag whether we are above the 'sm' (768px) breakpoint
 * @param data - Entity data
 * @param extendedMetadataConfig - The extended metadata config
 * @param updateDataMutation - Mutation used to update the data
 * @param currentUserRole - The role of the logged in user
 */

const ExtendedMetadata: React.FC<ExtendedMetadataProps> = ({
  t,
  isSmallDevice,
  data,
  extendedMetadataConfig,
  updateDataMutation,
  currentUserRole,
}: ExtendedMetadataProps) => {
  const { uid } = useParams<{ uid: string }>();
  const { showError, showSuccess } = useSnackbar();
  const [formData, setFormData] = useState<Record<string, any>>();

  const detailsExtMetadata = extendedMetadataConfig?.extMetadataConfig
    .map(
      (c: AssetTypeReadExternalExtMetadataConfig | RoleExtMetadataReadExternalExtMetadataConfig) =>
        c.extMetadataGroupConfig,
    )
    .filter((m) => m.uid === uid);

  const schemaKey = detailsExtMetadata[0].jsonElementName;
  const formTitle = detailsExtMetadata[0]?.displayName;
  const jsonSchema = generateJSONSchema(detailsExtMetadata, formData, currentUserRole);
  const uiSchema = generateUISchema(detailsExtMetadata, currentUserRole);
  const userCanOnlyRead = canOnlyReadExtMetadata(detailsExtMetadata[0], currentUserRole);

  useEffect(() => {
    if (data && data.extendedMetadata && data.extendedMetadata[schemaKey]) {
      setFormData({ [schemaKey]: data.extendedMetadata[schemaKey] });
    } else {
      setFormData({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, data]);

  const handleSubmit = async (e: ISubmitEvent<{ formData: Record<string, unknown> }>) => {
    try {
      await updateDataMutation.mutateAsync({ extendedMetadata: { ...data.extendedMetadata, ...e.formData } });
      showSuccess(t('ns.common:success:submit'));
    } catch (error) {
      showError(t('ns.common:error:submit'));
    }
  };

  const RJSFButton = (
    <LoadingButton
      form="ext-metadata-form"
      type="submit"
      variant="contained"
      color="primary"
      data-testid="RJSF-submit-btn"
      isLoading={updateDataMutation.isLoading}
    >
      {t('ns.common:save:button')}
      {updateDataMutation.isSuccess && setTimeout(updateDataMutation.reset, 5000) && <Check />}
    </LoadingButton>
  );

  return (
    <BasicPageTemplate
      isSmallDevice={isSmallDevice}
      testId="extended-metadata"
      smallDeviceTopRight={!userCanOnlyRead && RJSFButton}
    >
      {!isSmallDevice && (
        <Box display="flex" justifyContent="space-between">
          <Typography variant="h5">{formTitle}</Typography>
          {!userCanOnlyRead && RJSFButton}
        </Box>
      )}
      {formData && (
        <RJSF
          id="ext-metadata-form"
          schema={jsonSchema}
          formData={formData}
          uiSchema={uiSchema}
          onChange={({ formData }) => {
            updateDataMutation.reset();
            setFormData(formData);
          }}
          onSubmit={handleSubmit}
          showErrorList={false}
          noHtml5Validate={true}
          sx={
            !isSmallDevice
              ? {
                  '& fieldset > legend': {
                    display: 'none',
                  },
                }
              : {}
          }
        >
          <button style={{ display: 'none' }} />
        </RJSF>
      )}
    </BasicPageTemplate>
  );
};

export default ExtendedMetadata;
